import { PaletteColor, initPalette } from "../defines/colors"
import {
  Direction,
  ItemType,
  MapID,
  PlayerAction,
  PlayerBody,
  isPigment,
} from "../defines/enums"
import { isMotion } from "../defines/input"
import {
  BindablePigment,
  BinderSet,
  Collectible,
  CollectibleSet,
  CombatConfig,
  EquippableItem,
  MapItem,
  Palette,
  PigmentAttackResult,
  PlayerEquipment,
  PlayerVisits,
  Position,
  RenderConfig,
  RenderSymbol,
} from "../defines/types"
import {
  EquipmentDefaults,
  PlayerDefaults,
  TapestryDefaults,
} from "../defines/values"
import { Enemy } from "../entity/enemies/enemy"
import { Trap } from "../entity/enemies/other/trap"
import { Binder } from "../entity/items/binder"
import { Elixir } from "../entity/items/elixir"
import { Equipment } from "../entity/items/equipment"
import { Gizmo } from "../entity/items/gizmo"
import { Hint } from "../entity/items/hint"
import { Item } from "../entity/items/item"
import { Key } from "../entity/items/key"
import { Optic } from "../entity/items/optic"
import { Pigment } from "../entity/items/pigment"
import { PigmentDrop } from "../entity/items/pigmentdrop"
import { Residue } from "../entity/other/residue"
import {
  getRandomCommonCollectible,
  getRandomMythicCollectible,
  initCollectibleSet,
  isCollectible,
  isCommonCollectible,
  isMythicCollectible,
} from "../utils/collectibles"
import { getPaletteColor } from "../utils/color"
import * as Utils from "../utils/equipment"
import { buildGizmo } from "../utils/gizmo"
import { convertToPigment } from "../utils/item"
import { mapIDToString } from "../utils/map"
import { getLevelExperience } from "../utils/player"
import { generateRandomNumber } from "../utils/rng"
import { PlayerHint } from "./playerhint"

export class Player {
  public readonly VERSION = 1.0

  private _visits: PlayerVisits = {
    january: 0,
    february: 0,
    march: 0,
    april: 0,
    may: 0,
    june: 0,
    july: 0,
    august: 0,
    september: 0,
    october: 0,
    november: 0,
    december: 0,
  }

  private static DEFAULT_EQUIPMENT = [
    ItemType.EQUIPMENT_HEAD_NONE,
    ItemType.EQUIPMENT_TORSO_NONE,
    ItemType.EQUIPMENT_FEET_NONE,
    ItemType.EQUIPMENT_GIZMO_NONE,
    ItemType.EQUIPMENT_WEAPON_NONE,
  ]

  private _deaths: number = 0
  private _render: RenderConfig
  private _combat: CombatConfig
  private _level: number = 1
  private _experience: number = 0

  private _moved: boolean = false
  private _position: Position
  private _facing: Direction
  private _elixirs: number
  private _vision: number

  private _hints: PlayerHint[] = []
  private _commonCollectibles: CollectibleSet
  private _mythicCollectibles: CollectibleSet
  private _keys: MapID[] = []
  private _items: ItemType[] = []
  private _optics: ItemType[]
  private _currentOptic: number
  private _pigments: Palette
  private _pigmentsCollected: Palette
  private _binders: number
  private _bindersCollected: BinderSet

  private _equipment: PlayerEquipment = {
    head: EquipmentDefaults.NONE,
    torso: EquipmentDefaults.NONE,
    feet: EquipmentDefaults.NONE,
    leftHand: EquipmentDefaults.NONE,
    rightHand: EquipmentDefaults.NONE,
  }
  private _foundEquipment: EquippableItem[]

  private _gizmo: Gizmo | undefined = undefined

  // for Polychrome Optic
  private _canSeeAll: boolean = false
  // for Ring of Invincibility
  private _essenceInvincible: boolean = false
  // for Antitheft Charm
  private _pigmentInvincible: boolean = false

  constructor(position: Position) {
    this._position = { ...position }
    this._render = PlayerDefaults.RENDER
    this._combat = { ...PlayerDefaults.COMBAT.LEVEL1 }
    this._facing = Direction.RIGHT
    this._elixirs = PlayerDefaults.START_ELIXIRS
    this._vision = PlayerDefaults.START_VISION
    this._pigments = { ...initPalette() }
    this._pigmentsCollected = { ...initPalette() }
    this._binders = PlayerDefaults.START_BINDER
    this._bindersCollected = { ...PlayerDefaults.START_BINDERS_COLLECTED }
    this._commonCollectibles = { ...initCollectibleSet() }
    this._mythicCollectibles = { ...initCollectibleSet() }
    this._optics = [ItemType.OPTIC_NONE]
    this._currentOptic = 0
    this._foundEquipment = []

    Player.DEFAULT_EQUIPMENT.forEach((e) => {
      this.addEquipment(e)
    })

    this.equip(this._foundEquipment)
  }

  get deaths(): number {
    return this._deaths
  }

  get experience(): number {
    return this._experience
  }

  get level(): number {
    return this._level
  }

  get moved(): boolean {
    return this._moved
  }

  get optics(): ItemType[] {
    return this._optics
  }

  overwriteOptics(optics: ItemType[]): void {
    this._optics = optics
  }

  get stealth(): number {
    return this.computeStealthRange()
  }

  get bindersCollected(): BinderSet {
    return this._bindersCollected
  }

  getTotalBindersCollected(): number {
    return (
      this._bindersCollected.jan +
      this._bindersCollected.feb +
      this._bindersCollected.mar +
      this._bindersCollected.apr +
      this._bindersCollected.may +
      this._bindersCollected.jun +
      this._bindersCollected.jul +
      this._bindersCollected.aug +
      this._bindersCollected.sep +
      this._bindersCollected.oct +
      this._bindersCollected.nov +
      this._bindersCollected.dec
    )
  }

  getPigmentCollected(): Palette {
    return this._pigmentsCollected
  }

  visit(month: MapID): void {
    switch (month) {
      case MapID.January:
        this._visits.january += 1
        break
      case MapID.February:
        this._visits.february += 1
        break
      case MapID.March:
        this._visits.march += 1
        break
      case MapID.April:
        this._visits.april += 1
        break
      case MapID.May:
        this._visits.may += 1
        break
      case MapID.June:
        this._visits.june += 1
        break
      case MapID.July:
        this._visits.july += 1
        break
      case MapID.August:
        this._visits.august += 1
        break
      case MapID.September:
        this._visits.september += 1
        break
      case MapID.October:
        this._visits.october += 1
        break
      case MapID.November:
        this._visits.november += 1
        break
      case MapID.December:
        this._visits.december += 1
        break
    }
  }

  get visits(): PlayerVisits {
    return this._visits
  }

  getGizmosCount(): number {
    let gizmos = 0
    this._foundEquipment.forEach((e) => {
      if (e.type != ItemType.EQUIPMENT_GIZMO_NONE && Utils.isGizmo(e.type)) {
        gizmos += 1
      }
    })
    return gizmos
  }

  getWeaponsCount(): number {
    let weapons = 0
    this._foundEquipment.forEach((e) => {
      if (e.type != ItemType.EQUIPMENT_WEAPON_NONE && Utils.isWeapon(e.type)) {
        weapons += 1
      }
    })
    return weapons
  }

  getWearablesCount(): number {
    let wearables = 0
    this._foundEquipment.forEach((e) => {
      if (
        (e.type != ItemType.EQUIPMENT_HEAD_NONE &&
          Utils.isHeadEquipment(e.type)) ||
        (e.type != ItemType.EQUIPMENT_TORSO_NONE &&
          Utils.isTorsoEquipment(e.type)) ||
        (e.type != ItemType.EQUIPMENT_FEET_NONE &&
          Utils.isFeetEquipment(e.type))
      ) {
        wearables += 1
      }
    })
    return wearables
  }

  getNextLevelExpString(): string {
    if (this._level < PlayerDefaults.MAX_LEVEL) {
      const next = this._level + 1
      const nextExp = getLevelExperience(next)
      return `(next -> ${nextExp})`
    }

    return "(AT MAX)"
  }

  addDeath(): void {
    this._deaths += 1
  }

  addElixir(amount: number): number {
    if (this._elixirs >= PlayerDefaults.MAX_ELIXIRS) {
      return 0
    }

    let canAdd = PlayerDefaults.MAX_ELIXIRS - this._elixirs
    if (amount <= canAdd) {
      this._elixirs += amount
      return amount
    } else {
      this._elixirs += canAdd
      return canAdd
    }
  }

  addExperience(amount: number): void {
    this._experience += amount
    if (this._level < PlayerDefaults.MAX_LEVEL && this.shouldLevelUp()) {
      this.setLevel(this._level + 1)
    }
  }

  private shouldLevelUp(): boolean {
    if (this._experience >= PlayerDefaults.EXP_LEVEL10 && this._level == 9) {
      return true
    } else if (
      this._experience >= PlayerDefaults.EXP_LEVEL9 &&
      this._level == 8
    ) {
      return true
    } else if (
      this._experience >= PlayerDefaults.EXP_LEVEL8 &&
      this._level == 7
    ) {
      return true
    } else if (
      this._experience >= PlayerDefaults.EXP_LEVEL7 &&
      this._level == 6
    ) {
      return true
    } else if (
      this._experience >= PlayerDefaults.EXP_LEVEL6 &&
      this._level == 5
    ) {
      return true
    } else if (
      this._experience >= PlayerDefaults.EXP_LEVEL5 &&
      this._level == 4
    ) {
      return true
    } else if (
      this._experience >= PlayerDefaults.EXP_LEVEL4 &&
      this._level == 3
    ) {
      return true
    } else if (
      this._experience >= PlayerDefaults.EXP_LEVEL3 &&
      this._level == 2
    ) {
      return true
    } else if (
      this._experience >= PlayerDefaults.EXP_LEVEL2 &&
      this._level == 1
    ) {
      return true
    }
    return false
  }

  private setLevel(level: number): void {
    if (level == 10) {
      this._level = 10
      this._combat = { ...PlayerDefaults.COMBAT.LEVEL10 }
    } else if (level == 9) {
      this._level = 9
      this._combat = { ...PlayerDefaults.COMBAT.LEVEL9 }
    } else if (level == 8) {
      this._level = 8
      this._combat = { ...PlayerDefaults.COMBAT.LEVEL8 }
    } else if (level == 7) {
      this._level = 7
      this._combat = { ...PlayerDefaults.COMBAT.LEVEL7 }
    } else if (level == 6) {
      this._level = 6
      this._combat = { ...PlayerDefaults.COMBAT.LEVEL6 }
    } else if (level == 5) {
      this._level = 5
      this._combat = { ...PlayerDefaults.COMBAT.LEVEL5 }
    } else if (level == 4) {
      this._level = 4
      this._combat = { ...PlayerDefaults.COMBAT.LEVEL4 }
    } else if (level == 3) {
      this._level = 3
      this._combat = { ...PlayerDefaults.COMBAT.LEVEL3 }
    } else if (level == 2) {
      this._level = 2
      this._combat = { ...PlayerDefaults.COMBAT.LEVEL2 }
    } else if (level == 1) {
      this._level = 1
      this._combat = { ...PlayerDefaults.COMBAT.LEVEL1 }
    }
  }

  isAlive(): boolean {
    return this.combat.essence > 0
  }

  dealHit(): number {
    return this.computeEssenceAttack()
  }

  takeHit(amount: number): number {
    if (this._essenceInvincible) {
      return 0
    }

    const damage = amount - this.computeEssenceDefense()
    const damageTaken = damage > 0 ? damage : 0
    this.combat.essence -= damageTaken
    return damageTaken
  }

  private computeEssenceAttack(): number {
    let min = this.combat.minAttack
    let max = this.combat.maxAttack

    if (this._equipment.leftHand !== undefined) {
      min += this._equipment.leftHand.minEssenceAttack
      max += this._equipment.leftHand.maxEssenceAttack
    }
    if (this._equipment.rightHand !== undefined) {
      min += this._equipment.rightHand.minEssenceAttack
      max += this._equipment.rightHand.maxEssenceAttack
    }

    return generateRandomNumber(min, max)
  }

  private computeEssenceMaxAttack(): number {
    let maxAttack = this.combat.maxAttack

    if (this._equipment.leftHand !== undefined) {
      maxAttack += this._equipment.leftHand.maxEssenceAttack
    }
    if (this._equipment.rightHand !== undefined) {
      maxAttack += this._equipment.rightHand.maxEssenceAttack
    }

    return maxAttack
  }

  private computeEssenceDefense(): number {
    let defense = this.combat.essenceDefense
    if (this._equipment.head !== undefined) {
      defense += this._equipment.head.essenceDefense
    }
    if (this._equipment.torso !== undefined) {
      defense += this._equipment.torso.essenceDefense
    }
    if (this._equipment.feet !== undefined) {
      defense += this._equipment.feet.essenceDefense
    }

    return Math.min(PlayerDefaults.MAX_STAT, defense)
  }

  private computePigmentDefense(): number {
    let defense = this.combat.pigmentDefense ? this.combat.pigmentDefense : 0
    if (this._equipment.head !== undefined) {
      defense += this._equipment.head.pigmentDefense
    }
    if (this._equipment.torso !== undefined) {
      defense += this._equipment.torso.pigmentDefense
    }
    if (this._equipment.feet !== undefined) {
      defense += this._equipment.feet.pigmentDefense
    }

    return Math.min(PlayerDefaults.MAX_STAT, defense)
  }

  private computeStealthRange(): number {
    let range = this.combat.stealthRange ? this.combat.stealthRange : 0
    if (this._equipment.head !== undefined) {
      range += this._equipment.head.stealthRange
    }
    if (this._equipment.torso !== undefined) {
      range += this._equipment.torso.stealthRange
    }
    if (this._equipment.feet !== undefined) {
      range += this._equipment.feet.stealthRange
    }

    return Math.min(PlayerDefaults.MAX_STAT, range)
  }

  addCollectible(c: Collectible, mapID: MapID): boolean {
    const month = mapIDToString(mapID).substring(0, 3).toUpperCase()
    if (isCommonCollectible(c.type)) {
      return this.addCollectibleToMonth(c, this._commonCollectibles, month)
    } else if (isMythicCollectible(c.type)) {
      return this.addCollectibleToMonth(c, this._mythicCollectibles, month)
    }

    return false
  }

  addCollectibleToMonth(
    c: Collectible,
    set: CollectibleSet,
    month: string
  ): boolean {
    switch (month) {
      case "JAN":
        if (set.jan === undefined) {
          set.jan = c
          return true
        }
        break
      case "FEB":
        if (set.feb === undefined) {
          set.feb = c
          return true
        }
        break
      case "MAR":
        if (set.mar === undefined) {
          set.mar = c
          return true
        }
        break
      case "APR":
        if (set.apr === undefined) {
          set.apr = c
          return true
        }
        break
      case "MAY":
        if (set.may === undefined) {
          set.may = c
          return true
        }
        break
      case "JUN":
        if (set.jun === undefined) {
          set.jun = c
          return true
        }
        break
      case "JUL":
        if (set.jul === undefined) {
          set.jul = c
          return true
        }
        break
      case "AUG":
        if (set.aug === undefined) {
          set.aug = c
          return true
        }
        break
      case "SEP":
        if (set.sep === undefined) {
          set.sep = c
          return true
        }
        break
      case "OCT":
        if (set.oct === undefined) {
          set.oct = c
          return true
        }
        break
      case "NOV":
        if (set.nov === undefined) {
          set.nov = c
          return true
        }
        break
      case "DEC":
        if (set.dec === undefined) {
          set.dec = c
          return true
        }
        break
    }
    return false
  }

  getCollectibles(type: ItemType): Collectible[] {
    if (type == ItemType.COLLECTIBLE_COMMON) {
      return this.commonCollectibles
    } else if (type == ItemType.COLLECTIBLE_MYTHIC) {
      return this.mythicCollectibles
    }
    return []
  }

  get commonCollectibles(): Collectible[] {
    let collectibles: Collectible[] = []
    Object.entries(this._commonCollectibles).forEach(([_, coll]) => {
      if (coll !== undefined) {
        collectibles.push(coll)
      }
    })
    return collectibles
  }

  hasCommonCollectible(mapID: MapID): boolean {
    let result = false
    const mapStr = mapIDToString(mapID).substring(0, 3).toLowerCase()
    Object.entries(this._commonCollectibles).every(([month, coll]) => {
      if (mapStr == month && coll !== undefined) {
        result = true
        return false
      }
      return true
    })
    return result
  }

  get mythicCollectibles(): Collectible[] {
    let collectibles: Collectible[] = []
    Object.entries(this._mythicCollectibles).forEach(([_, coll]) => {
      if (coll !== undefined) {
        collectibles.push(coll)
      }
    })
    return collectibles
  }

  hasMythicCollectible(mapID: MapID): boolean {
    let result = false
    const mapStr = mapIDToString(mapID).substring(0, 3).toLowerCase()
    Object.entries(this._mythicCollectibles).every(([month, coll]) => {
      if (mapStr == month && coll !== undefined) {
        result = true
        return false
      }
      return true
    })
    return result
  }

  get equipment(): EquippableItem[] {
    return this._foundEquipment
  }

  addEquipment(i: ItemType): void {
    if (!this.hasEquipment(i)) {
      let equipment = Utils.getEquipment(i)
      if (equipment !== undefined) {
        this._foundEquipment.push({ ...equipment })
      }
    }
  }

  hasEquipment(e: ItemType): boolean {
    for (let i = 0; i < this._foundEquipment.length; ++i) {
      if (this._foundEquipment[i].type == e) {
        return true
      }
    }

    return false
  }

  // Items to avoid duplicates:
  // hints
  // keys
  // optics
  // equipment
  hasMapItem(item: MapItem): boolean {
    switch (item.type) {
      case ItemType.HINT_COLLECTIBLE:
      case ItemType.HINT_INFO:
      case ItemType.HINT_WARNING:
        if (item.textContents !== undefined) {
          return this.hasHint(item.textContents)
        }
        break
      case ItemType.EQUIPMENT_HEAD1:
      case ItemType.EQUIPMENT_HEAD2:
      case ItemType.EQUIPMENT_HEAD3:
      case ItemType.EQUIPMENT_HEAD4:
      case ItemType.EQUIPMENT_HEAD5:
      case ItemType.EQUIPMENT_HEAD6:
      case ItemType.EQUIPMENT_HEAD7:
      case ItemType.EQUIPMENT_HEAD8:
      case ItemType.EQUIPMENT_TORSO1:
      case ItemType.EQUIPMENT_TORSO2:
      case ItemType.EQUIPMENT_TORSO3:
      case ItemType.EQUIPMENT_TORSO4:
      case ItemType.EQUIPMENT_TORSO5:
      case ItemType.EQUIPMENT_TORSO6:
      case ItemType.EQUIPMENT_TORSO7:
      case ItemType.EQUIPMENT_TORSO8:
      case ItemType.EQUIPMENT_FEET1:
      case ItemType.EQUIPMENT_FEET2:
      case ItemType.EQUIPMENT_FEET3:
      case ItemType.EQUIPMENT_FEET4:
      case ItemType.EQUIPMENT_FEET5:
      case ItemType.EQUIPMENT_FEET6:
      case ItemType.EQUIPMENT_FEET7:
      case ItemType.EQUIPMENT_FEET8:
      case ItemType.EQUIPMENT_GIZMO1:
      case ItemType.EQUIPMENT_GIZMO2:
      case ItemType.EQUIPMENT_GIZMO3:
      case ItemType.EQUIPMENT_GIZMO4:
      case ItemType.EQUIPMENT_GIZMO5:
      case ItemType.EQUIPMENT_GIZMO6:
      case ItemType.EQUIPMENT_WEAPON1:
      case ItemType.EQUIPMENT_WEAPON2:
      case ItemType.EQUIPMENT_WEAPON3:
      case ItemType.EQUIPMENT_WEAPON4:
      case ItemType.EQUIPMENT_WEAPON5:
      case ItemType.EQUIPMENT_WEAPON6:
      case ItemType.EQUIPMENT_WEAPON7:
        return this.hasEquipment(item.type)
      case ItemType.KEY:
        if (item.keyTarget !== undefined) {
          return this.hasKey(item.keyTarget)
        }
        break
      case ItemType.OPTIC_RED:
      case ItemType.OPTIC_GREEN:
      case ItemType.OPTIC_BLUE:
      case ItemType.OPTIC_MAGENTA:
      case ItemType.OPTIC_CYAN:
      case ItemType.OPTIC_YELLOW:
        return this.hasOptic(item.type)
    }
    return false
  }

  private hasHint(find: string): boolean {
    const upFind = find.toUpperCase()
    for (let i = 0; i < this._hints.length; ++i) {
      const upHave = this._hints[i].contents.toUpperCase()
      if (upFind == upHave) {
        return true
      }
    }
    return false
  }

  addHint(hint: PlayerHint): boolean {
    if (
      !this._hints.find((c) => {
        return (
          c.type == hint.type &&
          c.contents == hint.contents &&
          c.year == hint.year &&
          c.month == hint.month &&
          c.dayOfMonth == hint.dayOfMonth
        )
      })
    ) {
      this._hints.unshift(hint)
      return true
    }

    return false
  }

  bindPigment(color: PaletteColor): boolean {
    if (!this.canBind(color)) {
      return false
    } else {
      switch (color) {
        case PaletteColor.RED:
          this._pigments.red -= TapestryDefaults.PIGMENT_PER_BIND
          break
        case PaletteColor.GREEN:
          this._pigments.green -= TapestryDefaults.PIGMENT_PER_BIND
          break
        case PaletteColor.BLUE:
          this._pigments.blue -= TapestryDefaults.PIGMENT_PER_BIND
          break
        case PaletteColor.YELLOW:
          this._pigments.yellow -= TapestryDefaults.PIGMENT_PER_BIND
          break
        case PaletteColor.MAGENTA:
          this._pigments.magenta -= TapestryDefaults.PIGMENT_PER_BIND
          break
        case PaletteColor.CYAN:
          this._pigments.cyan -= TapestryDefaults.PIGMENT_PER_BIND
          break
        default:
          return false
      }
      this._binders -= 1
      window.engine.requestPlayerSaveState()
      return true
    }
  }

  private canBind(color: PaletteColor): boolean {
    if (this._binders > 0) {
      switch (color) {
        case PaletteColor.RED:
          if (this._pigments.red >= TapestryDefaults.PIGMENT_PER_BIND) {
            return true
          }
          break
        case PaletteColor.GREEN:
          if (this._pigments.green >= TapestryDefaults.PIGMENT_PER_BIND) {
            return true
          }
          break
        case PaletteColor.BLUE:
          if (this._pigments.blue >= TapestryDefaults.PIGMENT_PER_BIND) {
            return true
          }
          break
        case PaletteColor.YELLOW:
          if (this._pigments.yellow >= TapestryDefaults.PIGMENT_PER_BIND) {
            return true
          }
          break
        case PaletteColor.MAGENTA:
          if (this._pigments.magenta >= TapestryDefaults.PIGMENT_PER_BIND) {
            return true
          }
          break
        case PaletteColor.CYAN:
          if (this._pigments.cyan >= TapestryDefaults.PIGMENT_PER_BIND) {
            return true
          }
          break
        default:
          return false
      }
    }
    return false
  }

  getBindablePigments(): BindablePigment[] {
    let bindables: BindablePigment[] = [
      { color: PaletteColor.RED, bindable: false },
      { color: PaletteColor.GREEN, bindable: false },
      { color: PaletteColor.BLUE, bindable: false },
      { color: PaletteColor.YELLOW, bindable: false },
      { color: PaletteColor.MAGENTA, bindable: false },
      { color: PaletteColor.CYAN, bindable: false },
    ]

    for (let i = 0; i < bindables.length; ++i) {
      if (this.canBind(bindables[i].color)) {
        bindables[i].bindable = true
      }
    }

    return bindables
  }

  getHints(type?: ItemType): PlayerHint[] {
    if (type === undefined) {
      return this._hints
    } else {
      return this._hints.filter((c) => {
        return c.type == type
      })
    }
  }

  addKey(key: MapID): void {
    if (!this._keys.includes(key)) {
      this._keys.push(key)
    }
  }

  hasKey(key: MapID): boolean {
    return this._keys.includes(key)
  }

  addBinder(id: MapID): void {
    this._binders += 1

    switch (id) {
      case MapID.January:
        this._bindersCollected.jan += 1
        break
      case MapID.February:
        this._bindersCollected.feb += 1
        break
      case MapID.March:
        this._bindersCollected.mar += 1
        break
      case MapID.April:
        this._bindersCollected.apr += 1
        break
      case MapID.May:
        this._bindersCollected.may += 1
        break
      case MapID.June:
        this._bindersCollected.jun += 1
        break
      case MapID.July:
        this._bindersCollected.jul += 1
        break
      case MapID.August:
        this._bindersCollected.aug += 1
        break
      case MapID.September:
        this._bindersCollected.sep += 1
        break
      case MapID.October:
        this._bindersCollected.oct += 1
        break
      case MapID.November:
        this._bindersCollected.nov += 1
        break
      case MapID.December:
        this._bindersCollected.dec += 1
        break
    }
  }

  getBinders(): number {
    return this._binders
  }

  getBindersCollected(id: MapID): number {
    switch (id) {
      case MapID.January:
        return this._bindersCollected.jan
      case MapID.February:
        return this._bindersCollected.feb
      case MapID.March:
        return this._bindersCollected.mar
      case MapID.April:
        return this._bindersCollected.apr
      case MapID.May:
        return this._bindersCollected.may
      case MapID.June:
        return this._bindersCollected.jun
      case MapID.July:
        return this._bindersCollected.jul
      case MapID.August:
        return this._bindersCollected.aug
      case MapID.September:
        return this._bindersCollected.sep
      case MapID.October:
        return this._bindersCollected.oct
      case MapID.November:
        return this._bindersCollected.nov
      case MapID.December:
        return this._bindersCollected.dec
    }

    return 0
  }

  get combat(): CombatConfig {
    return this._combat
  }

  get essence(): string {
    return this._combat.essence.toString()
  }

  equip(equipment: EquippableItem[]): void {
    equipment.forEach((e) => {
      if (Utils.isHeadEquipment(e.type)) {
        this.setEquipment(e, PlayerBody.HEAD)
      } else if (Utils.isTorsoEquipment(e.type)) {
        this.setEquipment(e, PlayerBody.TORSO)
      } else if (Utils.isFeetEquipment(e.type)) {
        this.setEquipment(e, PlayerBody.FEET)
      } else if (Utils.isGizmo(e.type)) {
        if (this.setEquipment(e, PlayerBody.LEFT_HAND)) {
          if (this._gizmo !== undefined) {
            this._gizmo.stop()
            this._gizmo = undefined
          }

          this._gizmo = buildGizmo(e.type, this)
          if (this._gizmo) {
            if (e.type == ItemType.EQUIPMENT_GIZMO3) {
              this._gizmo.callback = togglePolychromeOptic
            } else if (e.type == ItemType.EQUIPMENT_GIZMO4) {
              this._gizmo.callback = toggleEssenceInvincibility
            } else if (e.type == ItemType.EQUIPMENT_GIZMO5) {
              this._gizmo.callback = togglePigmentInvincibility
            }
          }
        }
      } else if (Utils.isWeapon(e.type)) {
        this.setEquipment(e, PlayerBody.RIGHT_HAND)
      }
    })
  }

  setEquipment(e: EquippableItem, loc: PlayerBody): boolean {
    let result = true

    switch (loc) {
      case PlayerBody.HEAD:
        this._equipment.head.equipped = false
        e.equipped = true
        this._equipment.head = e
        break
      case PlayerBody.TORSO:
        this._equipment.torso.equipped = false
        e.equipped = true
        this._equipment.torso = e
        break
      case PlayerBody.FEET:
        this._equipment.feet.equipped = false
        e.equipped = true
        this._equipment.feet = e
        break
      case PlayerBody.RIGHT_HAND:
        this._equipment.rightHand.equipped = false
        e.equipped = true
        this._equipment.rightHand = e
        if (e.twoHanded) {
          this._gizmo = undefined
          this._equipment.leftHand.equipped = false
          const e = this.getFoundEquipment(ItemType.EQUIPMENT_GIZMO_NONE)
          if (e !== undefined) {
            this._equipment.leftHand = e
          }
        }
        break
      case PlayerBody.LEFT_HAND:
        let canEquip = false
        if (this._equipment.rightHand == null) {
          canEquip = true
        } else if (!this._equipment.rightHand.twoHanded) {
          canEquip = true
        }

        if (canEquip) {
          this._equipment.leftHand.equipped = false
          e.equipped = true
          this._equipment.leftHand = e
        } else {
          result = false
        }
        break
    }

    return result
  }

  getEquipment(loc: PlayerBody): EquippableItem {
    switch (loc) {
      case PlayerBody.HEAD:
        return this._equipment.head
      case PlayerBody.TORSO:
        return this._equipment.torso
      case PlayerBody.FEET:
        return this._equipment.feet
      case PlayerBody.LEFT_HAND:
        return this._equipment.leftHand
      default:
        return this._equipment.rightHand
    }
  }

  getFoundEquipment(type: ItemType): EquippableItem | undefined {
    for (let i = 0; i < this._foundEquipment.length; ++i) {
      if (this._foundEquipment[i].type == type) {
        return this._foundEquipment[i]
      }
    }

    return undefined
  }

  // TODO: cap these stats? at 10?
  getEquipmentStats(): EquippableItem {
    let stats: EquippableItem = { ...EquipmentDefaults.NONE }

    stats.maxEssenceAttack = this.computeEssenceMaxAttack()
    stats.essenceDefense = this.computeEssenceDefense()
    stats.pigmentDefense = this.computePigmentDefense()
    stats.stealthRange = this.computeStealthRange()

    return stats
  }

  get position(): Position {
    return this._position
  }

  get elixirs(): number {
    return this._elixirs
  }

  get optic(): ItemType | undefined {
    let result = undefined
    if (this._optics.length > 0) {
      result = this._optics[this._currentOptic]
    }

    return result
  }

  get vision(): number {
    return this._vision
  }

  setPosition(p: Position) {
    this._position = { ...p }
  }

  addEssence(amount: number): void {
    if (this._combat.essence < PlayerDefaults.MAX_ESSENCE) {
      this._combat.essence += amount
      if (this._combat.essence > PlayerDefaults.MAX_ESSENCE) {
        this._combat.essence = PlayerDefaults.MAX_ESSENCE
      }
      window.engine.sendMessage("You gain essence and become more solid")
    }
  }

  useElixir(): boolean {
    if (this._elixirs == 0) {
      window.engine.sendMessage("You have no elixirs", false)
      return false
    } else if (this._combat.essence < this._combat.maxEssence) {
      this._combat.essence += PlayerDefaults.ELIXIR_HEALING
      if (this._combat.essence > this._combat.maxEssence) {
        this._combat.essence = this._combat.maxEssence
      }
      this._elixirs -= 1
      window.engine.sendMessage("You consume elixir and become more solid")
      return true
    }

    window.engine.sendMessage("You are already fully formed", false)
    return false
  }

  decrementPigment(c: PaletteColor, amount: number): void {
    switch (c) {
      case PaletteColor.RED:
        this._pigments.red -= amount
        if (this._pigments.red < 0) {
          this._pigments.red = 0
        }
        break
      case PaletteColor.GREEN:
        this._pigments.green -= amount
        if (this._pigments.green < 0) {
          this._pigments.green = 0
        }
        break
      case PaletteColor.BLUE:
        this._pigments.blue -= amount
        if (this._pigments.blue < 0) {
          this._pigments.blue = 0
        }
        break
      case PaletteColor.YELLOW:
        this._pigments.yellow -= amount
        if (this._pigments.yellow < 0) {
          this._pigments.yellow = 0
        }
        break
      case PaletteColor.MAGENTA:
        this._pigments.magenta -= amount
        if (this._pigments.magenta < 0) {
          this._pigments.magenta = 0
        }
        break
      case PaletteColor.CYAN:
        this._pigments.cyan -= amount
        if (this._pigments.cyan < 0) {
          this._pigments.cyan = 0
        }
        break

      default:
        break
    }
  }

  getPigment(c: PaletteColor): number {
    let amount: number = 0
    switch (c) {
      case PaletteColor.YELLOW:
        amount = this._pigments.yellow
        break
      case PaletteColor.RED:
        amount = this._pigments.red
        break
      case PaletteColor.MAGENTA:
        amount = this._pigments.magenta
        break
      case PaletteColor.BLUE:
        amount = this._pigments.blue
        break
      case PaletteColor.CYAN:
        amount = this._pigments.cyan
        break
      case PaletteColor.GREEN:
        amount = this._pigments.green
        break
      default:
        break
    }

    return amount
  }

  setPigment(c: PaletteColor, value: number): void {
    switch (c) {
      case PaletteColor.YELLOW:
        this._pigments.yellow = value
        break
      case PaletteColor.RED:
        this._pigments.red = value
        break
      case PaletteColor.MAGENTA:
        this._pigments.magenta = value
        break
      case PaletteColor.BLUE:
        this._pigments.blue = value
        break
      case PaletteColor.CYAN:
        this._pigments.cyan = value
        break
      case PaletteColor.GREEN:
        this._pigments.green = value
        break
      default:
        break
    }
  }

  meetsThreshhold(c: PaletteColor): boolean {
    switch (c) {
      case PaletteColor.YELLOW:
        return this._pigments.yellow >= PlayerDefaults.YELLOW_THRESHHOLD
      case PaletteColor.RED:
        return this._pigments.red >= PlayerDefaults.RED_THRESHHOLD
      case PaletteColor.MAGENTA:
        return this._pigments.magenta >= PlayerDefaults.MAGENTA_THRESHHOLD
      case PaletteColor.BLUE:
        return this._pigments.blue >= PlayerDefaults.BLUE_THRESHHOLD
      case PaletteColor.CYAN:
        return this._pigments.cyan >= PlayerDefaults.CYAN_THRESHHOLD
      case PaletteColor.GREEN:
        return this._pigments.green >= PlayerDefaults.GREEN_THRESHHOLD
    }

    return false
  }

  getColorCountString(c: PaletteColor): string {
    const colorString = this.getPigment(c)
      .toString()
      .padStart(PlayerDefaults.MAX_PIGMENT_CHARS, " ")
    return colorString
  }

  getBinderCountString(): string {
    return this._binders
      .toString()
      .padStart(PlayerDefaults.MAX_BINDER_CHARS, " ")
  }

  addItem(item: ItemType): boolean {
    if (!this.hasItem(item)) {
      this._items.push(item)
      return true
    }

    return false
  }

  hasItem(item: ItemType): boolean {
    return this._items.includes(item)
  }

  addOptic(optic: ItemType): void {
    if (!this._optics.includes(optic)) {
      this._optics.push(optic)
    }
  }

  hasOpticEquipped(optic: ItemType): boolean {
    return this._optics[this._currentOptic] == optic
  }

  hasOptic(optic: ItemType): boolean {
    return this._optics.includes(optic)
  }

  hasVision(optic: ItemType): boolean {
    if (this._canSeeAll) {
      return true
    }

    if (optic == ItemType.OPTIC_RED) {
      return this.hasOpticEquipped(optic) || this.hasPassiveVision(optic)
    } else if (optic == ItemType.OPTIC_GREEN) {
      return this.hasOpticEquipped(optic) || this.hasPassiveVision(optic)
    } else if (optic == ItemType.OPTIC_BLUE) {
      return this.hasOpticEquipped(optic) || this.hasPassiveVision(optic)
    } else if (optic == ItemType.OPTIC_YELLOW) {
      return (
        this.hasVision(ItemType.OPTIC_RED) &&
        this.hasVision(ItemType.OPTIC_GREEN)
      )
    } else if (optic == ItemType.OPTIC_MAGENTA) {
      return (
        this.hasVision(ItemType.OPTIC_RED) &&
        this.hasVision(ItemType.OPTIC_BLUE)
      )
    } else if (optic == ItemType.OPTIC_CYAN) {
      return (
        this.hasVision(ItemType.OPTIC_GREEN) &&
        this.hasVision(ItemType.OPTIC_BLUE)
      )
    }

    return false
  }

  addPigment(type: ItemType, amount: number): boolean {
    if (isPigment(type)) {
      const color = convertToPigment(type)
      this.addColor(color, amount)
      return true
    }

    return false
  }

  private hasPassiveVision(optic: ItemType): boolean {
    return false

    // let vision: boolean = false

    // if (
    //   optic == ItemType.OPTIC_YELLOW &&
    //   this._pigments.yellow >= PlayerDefaults.YELLOW_THRESHHOLD
    // ) {
    //   vision = true
    // } else if (
    //   optic == ItemType.OPTIC_MAGENTA &&
    //   this._pigments.magenta >= PlayerDefaults.MAGENTA_THRESHHOLD
    // ) {
    //   vision = true
    // } else if (
    //   optic == ItemType.OPTIC_RED &&
    //   this._pigments.red >= PlayerDefaults.RED_THRESHHOLD
    // ) {
    //   vision = true
    // } else if (
    //   optic == ItemType.OPTIC_BLUE &&
    //   this._pigments.blue >= PlayerDefaults.BLUE_THRESHHOLD
    // ) {
    //   vision = true
    // } else if (
    //   optic == ItemType.OPTIC_CYAN &&
    //   this._pigments.cyan >= PlayerDefaults.CYAN_THRESHHOLD
    // ) {
    //   vision = true
    // } else if (
    //   optic == ItemType.OPTIC_GREEN &&
    //   this._pigments.green >= PlayerDefaults.GREEN_THRESHHOLD
    // ) {
    //   vision = true
    // }

    // return vision
  }

  addColor(color: PaletteColor, amount: number = 1): void {
    let toAdd = 0
    switch (color) {
      case PaletteColor.RED:
        if (this._pigments.red + amount <= PlayerDefaults.MAX_PIGMENT) {
          toAdd = amount
        } else {
          toAdd = PlayerDefaults.MAX_PIGMENT - this._pigments.red
        }
        this._pigments.red += toAdd
        this._pigmentsCollected.red += toAdd
        break
      case PaletteColor.GREEN:
        if (this._pigments.green + amount <= PlayerDefaults.MAX_PIGMENT) {
          toAdd = amount
        } else {
          toAdd = PlayerDefaults.MAX_PIGMENT - this._pigments.green
        }
        this._pigments.green += toAdd
        this._pigmentsCollected.green += toAdd
        break
      case PaletteColor.BLUE:
        if (this._pigments.blue + amount <= PlayerDefaults.MAX_PIGMENT) {
          toAdd = amount
        } else {
          toAdd = PlayerDefaults.MAX_PIGMENT - this._pigments.blue
        }
        this._pigments.blue += toAdd
        this._pigmentsCollected.blue += toAdd
        break
      case PaletteColor.YELLOW:
        if (this._pigments.yellow + amount <= PlayerDefaults.MAX_PIGMENT) {
          toAdd = amount
        } else {
          toAdd = PlayerDefaults.MAX_PIGMENT - this._pigments.yellow
        }
        this._pigments.yellow += toAdd
        this._pigmentsCollected.yellow += toAdd
        break
      case PaletteColor.MAGENTA:
        if (this._pigments.magenta + amount <= PlayerDefaults.MAX_PIGMENT) {
          toAdd = amount
        } else {
          toAdd = PlayerDefaults.MAX_PIGMENT - this._pigments.magenta
        }
        this._pigments.magenta += toAdd
        this._pigmentsCollected.magenta += toAdd
        break
      case PaletteColor.CYAN:
        if (this._pigments.cyan + amount <= PlayerDefaults.MAX_PIGMENT) {
          toAdd = amount
        } else {
          toAdd = PlayerDefaults.MAX_PIGMENT - this._pigments.cyan
        }
        this._pigments.cyan += toAdd
        this._pigmentsCollected.cyan += toAdd
        break
      default:
        break
    }

    window.engine.requestPlayerSaveState()
  }

  private getPigmentsOfAmount(amount: number): string[] {
    let pigments: string[] = []

    Object.entries(this._pigments).forEach(([color, quant]) => {
      if (quant >= amount) {
        pigments.push(color)
      }
    })

    return pigments
  }

  private loseColorFromPalette(amount: number, color: string): number {
    if (amount <= this.getPigment(getPaletteColor(color))) {
      this.decrementPigment(getPaletteColor(color), amount)
      return amount
    } else {
      const amountLost = this.getPigment(getPaletteColor(color))
      this.setPigment(getPaletteColor(color), 0)
      return amountLost
    }
  }

  loseColor(amount: number): PigmentAttackResult[] {
    let result: PigmentAttackResult[] = []

    let amountToLose: number = amount - this.computePigmentDefense()
    if (this._pigmentInvincible || amountToLose <= 0) {
      return result
    }

    let pigmentsCanLose = this.getPigmentsOfAmount(amountToLose)
    if (pigmentsCanLose.length > 0) {
      const idx = generateRandomNumber(0, pigmentsCanLose.length - 1)
      amountToLose = this.loseColorFromPalette(
        amountToLose,
        pigmentsCanLose[idx]
      )
      result.push({ amount: amountToLose, color: pigmentsCanLose[idx] })
    } else {
      // take pigment from different colors until amount is reached, or
      // player runs out of pigments
      pigmentsCanLose = this.getPigmentsOfAmount(1)

      // sort by most to least
      pigmentsCanLose.sort((a, b) => {
        return (
          this.getPigment(getPaletteColor(a)) -
          this.getPigment(getPaletteColor(b))
        )
      })

      let remains = amountToLose
      let justLost = 0
      pigmentsCanLose.every((p) => {
        if (remains > 0) {
          justLost = this.loseColorFromPalette(remains, p)
          remains -= justLost
          result.push({ amount: justLost, color: p })
          return true
        }
        return false
      })
    }

    return result
  }

  resurrect(): void {
    this.addDeath()
    this.setLevel(this._level)
  }

  renderDark(): RenderSymbol {
    const symbol = this._render.symbol[0]
    return { symbol: symbol, color: this._render.dark }
  }

  renderLight(): RenderSymbol {
    const symbol = this._render.symbol[0]
    return { symbol: symbol, color: this._render.light }
  }

  update(action: PlayerAction): void {
    this._moved = false

    let stateUpdated = false

    if (action == PlayerAction.INTERACT) {
      const interactables = window.engine.getNearestInteractables(
        this.position,
        this._facing
      )
      if (interactables.length > 0) {
        window.engine.interact(interactables[0])
      }

      return
    }

    if (action == PlayerAction.USE_ELIXIR) {
      this.useElixir()
      stateUpdated = true
      return
    }

    if (action == PlayerAction.USE_GIZMO) {
      this.activateGizmo(this._equipment.leftHand.type)
      return
    }

    if (action == PlayerAction.CYCLE_OPTIC) {
      if (this._optics.length > 0) {
        this._currentOptic += 1
        if (this._currentOptic >= this._optics.length) {
          this._currentOptic = 0
        }
      }
      return
    }

    if (isMotion(action)) {
      let targetPosition = this.projectMove(action)

      const group = window.engine.getEntitiesAtLocation(targetPosition)
      group.forEach((e) => {
        if (e) {
          if (!e.blocks) {
            if (e instanceof PigmentDrop) {
              const aStr = e.amount.toString()
              const message = "You collected " + aStr + " " + e.displayName
              window.engine.sendMessage(message)
              this.addColor(convertToPigment(e.type), e.amount)
              window.engine.removeEntityAtLocation(targetPosition)
              stateUpdated = true
            } else if (e instanceof Pigment) {
              if (e.optic && this.hasVision(e.optic)) {
                window.engine.sendMessage("You collected " + e.displayName)
                if (e.optic) {
                  this.addColor(convertToPigment(e.optic))
                  stateUpdated = true
                }
                window.engine.removeEntityAtLocation(targetPosition)
              } else {
                window.engine.sendMessage(
                  "You sense something interesting here"
                )
              }
            } else if (e instanceof Item) {
              let canHave = true
              let message = "You found "
              if (e instanceof Binder) {
                message += e.displayName
                this.addBinder(window.engine.currentMapID)
                stateUpdated = true
              } else if (e instanceof Elixir) {
                message += e.displayName
                this.addElixir(1)
              } else if (e instanceof Key) {
                message += e.displayName
                this.addKey(e.target)
                stateUpdated = true
              } else if (e instanceof Hint) {
                this.addHint(new PlayerHint(e.type, e.contents))
                stateUpdated = true
                window.engine.removeItemAtLocation(targetPosition, e.type)
                message = "A hint! " + e.contents
              } else if (e instanceof Optic) {
                message += e.displayName
                this.addOptic(e.type)
                stateUpdated = true
              } else if (e instanceof Equipment && !this.hasEquipment(e.type)) {
                if (this._level >= e.levelRequired) {
                  message += e.displayName
                  this.addEquipment(e.type)
                  stateUpdated = true
                } else {
                  canHave = false
                  message =
                    "You must be level " +
                    e.levelRequired +
                    " to have " +
                    e.displayName
                }
              } else if (isCollectible(e.type)) {
                if (isCommonCollectible(e.type)) {
                  const coll = getRandomCommonCollectible(this)
                  if (coll !== undefined) {
                    message += coll.name + " (" + e.displayName + ")"
                    this.addCollectible(coll, window.engine.currentMapID)
                    stateUpdated = true
                  }
                } else if (isMythicCollectible(e.type)) {
                  const coll = getRandomMythicCollectible(this)
                  if (coll !== undefined) {
                    message += coll.name + " (" + e.displayName + ")"
                    this.addCollectible(coll, window.engine.currentMapID)
                    stateUpdated = true
                  }
                }
              }

              if (canHave) {
                window.engine.removeItemAtLocation(targetPosition, e.type)
                window.engine.sendMessage(message)
              }
            } else if (e instanceof Residue) {
              window.engine.sendMessage("Remains of " + e.displayName)
            }
          } else {
            targetPosition = this.position
            if (e.baseClass == "Enemy") {
              if (e instanceof Trap) {
                window.engine.sendMessage(
                  "A trap drains you of all essence; a wind sweeps you away"
                )
                this.combat.essence = 0
              } else {
                window.engine.processPlayerAttack(e as Enemy, true)
              }
            } else {
              window.engine.sendMessage(e.displayName, false)
            }
          }
        }
      })

      if (stateUpdated) {
        window.engine.requestPlayerSaveState()
      }

      if (this.isAlive()) {
        if (
          targetPosition.x != this._position.x ||
          targetPosition.y != this._position.y
        ) {
          this._position = targetPosition
          this._moved = true
        }
      }
    }
  }

  private activateGizmo(type: ItemType): void {
    if (
      this._gizmo !== undefined &&
      (this._gizmo.active || this._gizmo.onCooldown)
    ) {
      return
    }

    switch (type) {
      case ItemType.EQUIPMENT_GIZMO_NONE:
        return

      case ItemType.EQUIPMENT_GIZMO1:
        // Escape Rope
        window.engine.sendMessage(
          "You have activated " + EquipmentDefaults.GIZMO.GIZMO1.name
        )
        window.engine.enterPortal(MapID.Portals)
        break
      case ItemType.EQUIPMENT_GIZMO2:
        // Map Scanner
        window.engine.sendMessage(
          "You have activated " + EquipmentDefaults.GIZMO.GIZMO2.name
        )
        window.engine.showMapSummaryStats()
        break
      case ItemType.EQUIPMENT_GIZMO3:
        // Polychrome Optic
        if (this._gizmo) {
          window.engine.sendMessage(
            "You have activated " + EquipmentDefaults.GIZMO.GIZMO3.name
          )
          this._gizmo.activate()
          this._canSeeAll = true
        }
        break
      case ItemType.EQUIPMENT_GIZMO4:
        // Ring of Invincibility
        if (this._gizmo) {
          window.engine.sendMessage(
            "You have activated " + EquipmentDefaults.GIZMO.GIZMO4.name
          )
          this._gizmo.activate()
          this._essenceInvincible = true
        }
        break
      case ItemType.EQUIPMENT_GIZMO5:
        // Antitheft Charm
        if (this._gizmo) {
          window.engine.sendMessage(
            "You have activated " + EquipmentDefaults.GIZMO.GIZMO5.name
          )
          this._gizmo.activate()
          this._pigmentInvincible = true
        }
        break
    }

    window.engine.requestUpdate()
  }

  toggleEssenceInvincibility(): void {
    if (this._essenceInvincible) {
      this._essenceInvincible = false
    } else if (this._gizmo !== undefined) {
      this._gizmo.stop()
    }
    window.engine.requestUpdate()
  }

  togglePigmentInvincibility(): void {
    if (this._pigmentInvincible) {
      this._pigmentInvincible = false
    } else if (this._gizmo !== undefined) {
      this._gizmo.stop()
    }
    window.engine.requestUpdate()
  }

  togglePolychromeOptic(): void {
    if (this._canSeeAll) {
      this._canSeeAll = false
    } else if (this._gizmo !== undefined) {
      this._gizmo.stop()
    }
    window.engine.requestUpdate()
  }

  getGizmoStats(): string {
    if (
      this._gizmo === undefined ||
      this._gizmo.type == ItemType.EQUIPMENT_GIZMO_NONE
    ) {
      return "none"
    }

    let stats = ""

    if (this._gizmo.type == ItemType.EQUIPMENT_GIZMO1) {
      stats = EquipmentDefaults.GIZMO.GIZMO1.name
    } else if (this._gizmo.type == ItemType.EQUIPMENT_GIZMO2) {
      stats = EquipmentDefaults.GIZMO.GIZMO2.name
    } else if (this._gizmo.type == ItemType.EQUIPMENT_GIZMO3) {
      stats = EquipmentDefaults.GIZMO.GIZMO3.name
    } else if (this._gizmo.type == ItemType.EQUIPMENT_GIZMO4) {
      stats = EquipmentDefaults.GIZMO.GIZMO4.name
    } else if (this._gizmo.type == ItemType.EQUIPMENT_GIZMO5) {
      stats = EquipmentDefaults.GIZMO.GIZMO5.name
    }

    if (this._gizmo.active) {
      stats += " (%c{#ff0}%b{#000}active%c{#fff}%b{#000})"
    } else if (this._gizmo.onCooldown) {
      stats += " (%c{#f00}%b{#000}cooldown%c{#fff}%b{#000})"
    } else {
      stats += " (%c{#0f0}%b{#000}ready%c{#fff}%b{#000})"
    }

    return stats
  }

  private projectMove(action: PlayerAction): Position {
    // let nextPosition = cloneDeep(this.position)
    let nextPosition = { ...this.position }
    if (
      action == PlayerAction.MOVE_LEFT &&
      window.engine.inBounds(nextPosition.x - 1, nextPosition.y)
    ) {
      this._facing = Direction.LEFT
      nextPosition.x -= 1
    } else if (
      action == PlayerAction.MOVE_RIGHT &&
      window.engine.inBounds(nextPosition.x + 1, nextPosition.y)
    ) {
      this._facing = Direction.RIGHT
      nextPosition.x += 1
    } else if (
      action == PlayerAction.MOVE_UP &&
      window.engine.inBounds(nextPosition.x, nextPosition.y - 1)
    ) {
      this._facing = Direction.UP
      nextPosition.y -= 1
    } else if (
      action == PlayerAction.MOVE_DOWN &&
      window.engine.inBounds(nextPosition.x, nextPosition.y + 1)
    ) {
      this._facing = Direction.DOWN
      nextPosition.y += 1
    }

    return nextPosition
  }
}

// GIZMO callbacks
function toggleEssenceInvincibility(player: Player): void {
  player.toggleEssenceInvincibility()
}

function togglePigmentInvincibility(player: Player): void {
  player.togglePigmentInvincibility()
}

function togglePolychromeOptic(player: Player): void {
  player.togglePolychromeOptic()
}
